<script setup>
import { useRouter } from 'vue-router'

const router = useRouter()

const goToWaitlist = () => {
  router.push({ name: 'home', hash: '#waitlist' })
}

const tiers = [
  {
    id: 'solo',
    name: 'Solo',
    tagline: 'For individual developers.',
    price: 'Free',
    priceNote: 'Always',
    seats: '1 seat',
    support: 'Community support',
    dataHome: 'All local. Your Mac, your data.',
    highlights: [
      'Local microVMs on macOS',
      'Devcontainer + compose support',
      'Local builds + artifact cache',
      'Basic policy enforcement',
    ],
    cta: { label: 'Install krate', kind: 'secondary', href: 'https://github.com/krate-dot-sh/krate-releases#install' },
    border: 'var(--color-krate-dark-200)',
    bg: 'transparent',
  },
  {
    id: 'team',
    name: 'Team',
    tagline: 'For small teams.',
    price: 'Coming soon',
    priceNote: 'Per-seat pricing',
    seats: 'Unlimited seats',
    support: 'Business-hours support',
    dataHome: 'Hosted krate server. Never sold or shared.',
    highlights: [
      'Everything in Solo, plus:',
      'Hosted build cluster',
      'Hosted OCI registry',
      'SSO (Google · Okta · Azure AD)',
      'Org / team hierarchy + shared volumes',
      'Remote policy sync',
    ],
    cta: { label: 'Notify me', kind: 'primary', onClick: goToWaitlist },
    border: 'var(--color-krate-blue-600)',
    bg: 'var(--color-krate-blue-50)',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For organizations.',
    price: 'Contact us',
    priceNote: 'Annual contract',
    seats: 'Unlimited seats',
    support: '24×5 SLA',
    dataHome: 'Self-hosted in your infrastructure.',
    highlights: [
      'Everything in Team, plus:',
      'Self-hosted (Helm chart)',
      'Customer-hosted OCI registry',
      'SLSA Level 3 attestation',
      'SBOM + vulnerability scanning',
      'Compliance reports',
      'Remote policy server',
    ],
    cta: { label: 'Contact sales', kind: 'secondary', href: 'mailto:hello@krate.sh?subject=Enterprise%20interest' },
    border: 'var(--color-krate-dark-200)',
    bg: 'transparent',
  },
]

const features = [
  { group: 'Workload runtime', items: [
    { name: 'Local microVMs (macOS)', solo: true, team: true, enterprise: true },
    { name: 'Devcontainer compatibility', solo: true, team: true, enterprise: true },
    { name: 'Compose support', solo: true, team: true, enterprise: true },
    { name: 'Basic policy enforcement', solo: true, team: true, enterprise: true },
    { name: 'Local audit log', solo: true, team: true, enterprise: true },
  ]},
  { group: 'Builds & artifacts', items: [
    { name: 'Local builds + cache', solo: true, team: true, enterprise: true },
    { name: 'Hosted build cluster', solo: false, team: 'Kannister-hosted', enterprise: 'Customer-hosted' },
    { name: 'Hosted OCI registry', solo: false, team: 'Kannister-hosted', enterprise: 'Customer-hosted' },
  ]},
  { group: 'Collaboration & identity', items: [
    { name: 'GitHub login', solo: true, team: true, enterprise: true },
    { name: 'Org / team hierarchy', solo: false, team: true, enterprise: true },
    { name: 'Shared volumes across team', solo: false, team: true, enterprise: true },
    { name: 'Remote policy sync', solo: false, team: true, enterprise: true },
    { name: 'SSO (Google · Okta · Azure AD)', solo: false, team: true, enterprise: true },
  ]},
  { group: 'Compliance & supply chain', items: [
    { name: 'SBOM generation', solo: false, team: false, enterprise: true },
    { name: 'Vulnerability scanning', solo: false, team: false, enterprise: true },
    { name: 'SLSA Level 3 attestation', solo: false, team: false, enterprise: true },
    { name: 'Compliance reports', solo: false, team: false, enterprise: true },
    { name: 'Remote policy server (self-hosted)', solo: false, team: false, enterprise: true },
  ]},
  { group: 'Deployment', items: [
    { name: 'Self-hosted (Helm chart)', solo: false, team: false, enterprise: true },
  ]},
]

function cellLabel(value) {
  if (value === true) return '✓'
  if (value === false) return '—'
  return value
}
function cellColor(value) {
  if (value === true) return 'var(--color-krate-blue-600)'
  if (value === false) return 'var(--color-krate-dark-300)'
  return 'var(--color-krate-dark-700)'
}
</script>

<template>
  <main class="bg-white pt-24 pb-20 px-4">
    <div class="max-w-7xl mx-auto">

      <header class="text-center mb-16">
        <h1 class="text-5xl md:text-6xl font-bold tracking-tight" style="color: var(--color-krate-dark-900);">Pricing</h1>
        <p class="mt-4 text-lg" style="color: var(--color-krate-dark-600);">
          Open source on every tier. Local microVMs are free, forever.
        </p>
      </header>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
        <article
          v-for="tier in tiers"
          :key="tier.id"
          class="rounded-xl border-2 p-8 flex flex-col"
          :style="{ borderColor: tier.border, backgroundColor: tier.bg }"
        >
          <header class="mb-6">
            <h2 class="text-2xl font-bold" style="color: var(--color-krate-dark-900);">{{ tier.name }}</h2>
            <p class="text-sm mt-1" style="color: var(--color-krate-dark-600);">{{ tier.tagline }}</p>
          </header>

          <div class="mb-6">
            <p class="text-4xl font-bold" style="color: var(--color-krate-blue-600);">{{ tier.price }}</p>
            <p class="text-sm mt-1" style="color: var(--color-krate-dark-500);">{{ tier.priceNote }} · {{ tier.seats }}</p>
            <p class="text-sm mt-1" style="color: var(--color-krate-dark-500);">{{ tier.support }}</p>
          </div>

          <div class="mb-6 rounded-md px-3 py-2 text-xs" style="background-color: var(--color-krate-dark-50); border: 1px solid var(--color-krate-dark-200);">
            <div class="font-semibold uppercase tracking-wider mb-0.5" style="color: var(--color-krate-dark-500); font-size: 0.65rem;">Where your data lives</div>
            <div style="color: var(--color-krate-dark-700);">{{ tier.dataHome }}</div>
          </div>

          <ul class="flex-1 space-y-2.5 mb-8">
            <li v-for="h in tier.highlights" :key="h" class="flex gap-2 text-sm" style="color: var(--color-krate-dark-700);">
              <span style="color: var(--color-krate-blue-600);" class="flex-shrink-0">•</span>
              <span>{{ h }}</span>
            </li>
          </ul>

          <a
            v-if="tier.cta.href"
            :href="tier.cta.href"
            target="_blank"
            rel="noopener"
            data-umami-event="cta-pricing"
            :data-umami-event-tier="tier.name"
            :data-umami-event-label="tier.cta.label"
            class="block text-center text-sm font-semibold px-5 py-3 rounded-md transition-colors"
            :style="tier.cta.kind === 'primary'
              ? 'background-color: var(--color-krate-blue-600); color: white;'
              : 'border: 1px solid var(--color-krate-dark-300); color: var(--color-krate-dark-900);'"
          >
            {{ tier.cta.label }}
          </a>
          <button
            v-else
            @click="tier.cta.onClick"
            data-umami-event="cta-pricing"
            :data-umami-event-tier="tier.name"
            :data-umami-event-label="tier.cta.label"
            class="block w-full text-center text-sm font-semibold px-5 py-3 rounded-md transition-colors"
            :style="tier.cta.kind === 'primary'
              ? 'background-color: var(--color-krate-blue-600); color: white;'
              : 'border: 1px solid var(--color-krate-dark-300); color: var(--color-krate-dark-900);'"
          >
            {{ tier.cta.label }}
          </button>
        </article>
      </div>

      <section class="mb-20">
        <header class="text-center mb-10">
          <h2 class="text-3xl md:text-4xl font-bold mb-3" style="color: var(--color-krate-dark-900);">
            Privacy scales with your <span style="color: var(--color-krate-blue-600);">tier</span>
          </h2>
          <p class="text-base max-w-2xl mx-auto" style="color: var(--color-krate-dark-600);">
            Most tools give the vendor more visibility as you pay more. krate goes the other way — sovereignty grows with the tier.
          </p>
        </header>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div class="rounded-lg p-6 border" style="border-color: var(--color-krate-dark-200);">
            <div class="text-xs uppercase tracking-wider font-semibold mb-2" style="color: var(--color-krate-blue-600);">Solo</div>
            <h3 class="text-lg font-semibold mb-2" style="color: var(--color-krate-dark-900);">Local</h3>
            <p class="text-sm" style="color: var(--color-krate-dark-600);">Everything runs on your Mac. We receive nothing about your activity.</p>
          </div>
          <div class="rounded-lg p-6 border-2" style="border-color: var(--color-krate-blue-600); background-color: var(--color-krate-blue-50);">
            <div class="text-xs uppercase tracking-wider font-semibold mb-2" style="color: var(--color-krate-blue-600);">Team</div>
            <h3 class="text-lg font-semibold mb-2" style="color: var(--color-krate-dark-900);">Hosted, managed</h3>
            <p class="text-sm" style="color: var(--color-krate-dark-700);">Audit, policy events, and pushed artifacts live on your hosted krate server. Never sold, shared, or used for our own analytics.</p>
          </div>
          <div class="rounded-lg p-6 border" style="border-color: var(--color-krate-dark-200);">
            <div class="text-xs uppercase tracking-wider font-semibold mb-2" style="color: var(--color-krate-blue-600);">Enterprise</div>
            <h3 class="text-lg font-semibold mb-2" style="color: var(--color-krate-dark-900);">Self-hosted</h3>
            <p class="text-sm" style="color: var(--color-krate-dark-600);">Everything in your own infrastructure. Data never leaves your perimeter.</p>
          </div>
        </div>
      </section>

      <section class="mb-12 text-center">
        <h2 class="text-3xl font-bold mb-2" style="color: var(--color-krate-dark-900);">Compare every feature</h2>
        <p class="text-sm" style="color: var(--color-krate-dark-500);">The full matrix, if you'd rather scan it.</p>
      </section>

      <div class="overflow-x-auto rounded-xl border" style="border-color: var(--color-krate-dark-200);">
        <table class="w-full text-sm">
          <thead class="sticky top-0">
            <tr style="background-color: var(--color-krate-dark-50);">
              <th class="text-left py-4 px-6 w-1/2 text-xs uppercase tracking-wider font-semibold" style="color: var(--color-krate-dark-500);">Feature</th>
              <th class="text-center py-4 px-4 text-sm font-semibold" style="color: var(--color-krate-dark-900);">Solo</th>
              <th class="text-center py-4 px-4 text-sm font-semibold" style="color: var(--color-krate-blue-700); background-color: var(--color-krate-blue-50);">Team</th>
              <th class="text-center py-4 px-4 text-sm font-semibold" style="color: var(--color-krate-dark-900);">Enterprise</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="group in features" :key="group.group">
              <tr style="background-color: var(--color-krate-dark-50);">
                <td colspan="4" class="py-3 px-6 text-xs uppercase tracking-wider font-semibold" style="color: var(--color-krate-dark-700);">
                  {{ group.group }}
                </td>
              </tr>
              <tr
                v-for="(item, i) in group.items"
                :key="item.name"
                :style="{ backgroundColor: i % 2 === 0 ? 'white' : 'var(--color-krate-dark-50)' }"
              >
                <td class="py-3 px-6" style="color: var(--color-krate-dark-700);">{{ item.name }}</td>
                <td class="text-center py-3 px-4 font-medium" :style="{ color: cellColor(item.solo) }">{{ cellLabel(item.solo) }}</td>
                <td class="text-center py-3 px-4 font-medium" :style="{ color: cellColor(item.team), backgroundColor: 'var(--color-krate-blue-50)' }">{{ cellLabel(item.team) }}</td>
                <td class="text-center py-3 px-4 font-medium" :style="{ color: cellColor(item.enterprise) }">{{ cellLabel(item.enterprise) }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <p class="text-center text-sm mt-12" style="color: var(--color-krate-dark-500);">
        Solo is free and always will be. Team pricing lands when the beta closes.
      </p>

    </div>
  </main>
</template>
